import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createReport } from "../../redux/Actions/ProfessionalMonitoringActions";
import { Button } from "../ui/Button";
import { X, AlertTriangle } from "lucide-react";
import { RootState, AppDispatch } from "../../redux/store";

interface ReportProfessionalModalProps {
  professional: any;
  contractId?: string;
  jobId?: string;
  onClose: () => void;
}

const ReportProfessionalModal: React.FC<ReportProfessionalModalProps> = ({ 
  professional, 
  contractId, 
  jobId,
  onClose 
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const client = useSelector((state: RootState) => state.auth.user);
  const [reportType, setReportType] = useState("off_platform_activity");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!description.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await dispatch(createReport({
        professionalId: professional.id || professional._id,
        clientId: client.id,
        contractId,
        jobId,
        reportType,
        description,
      })).unwrap();
      
      onClose();
    } catch (error) {
      console.error("Failed to submit report:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
        <div className="p-6 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <h2 className="text-xl font-semibold">Report Professional</h2>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Professional
            </label>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium">
                {professional.firstName} {professional.lastName}
              </p>
              <p className="text-sm text-gray-600">{professional.email}</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Type <span className="text-red-600">*</span>
            </label>
            <select 
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="off_platform_activity">Off-Platform Activity</option>
              <option value="contact_sharing">Contact Sharing / External Contact</option>
              <option value="poor_service">Poor Service Quality</option>
              <option value="no_show">No Show / Didn&apos;t Appear</option>
              <option value="other">Other Issue</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-600">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={5}
              placeholder="Please provide detailed information about the issue..."
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Minimum 20 characters
            </p>
          </div>
          
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-gray-700">
                <p className="font-medium mb-1">Important Notice</p>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>All reports are reviewed by our team</li>
                  <li>False reports may result in action against your account</li>
                  <li>This report will be logged and may affect the professional&apos;s standing</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-6 border-t bg-gray-50 rounded-b-xl flex gap-3">
          <Button 
            variant="outline" 
            onClick={onClose} 
            className="flex-1"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            disabled={isSubmitting || description.trim().length < 20}
          >
            {isSubmitting ? "Submitting..." : "Submit Report"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReportProfessionalModal;

